// Imports
// React and React-specific imports
import { useState } from 'react';

// Components
import { CalendarControls } from '../modules/calendar/components/CalendarControls';
import { CalendarView } from '../modules/calendar/components/CalendarView';
import { CalendarStatus } from '../modules/calendar/components/CalendarStatus';
import { TabButton } from '../modules/calendar/components/common/TabButton';

// Hooks
import { useCalendar } from '../services/calendar/hooks/useCalendar';

type MobileTab = 'Controls' | 'Calendar' | 'Status';

// Component Definition
const CalendarPage: React.FC = () => {
  const {
    availableFilter,
    selectedDaysOfWeek,
    handleDayClick,
    activeMonth,
    setActiveMonth,
    handleDayOfWeekToggle,
    handleAvailableFilterChange,
    recentlyClickedDate,
    statusViewFilter,
    setStatusViewFilter,
    statusFilteredDates,
  } = useCalendar();

  // Set initial tab to 'Calendar' for mobile
  const [mobileTab, setMobileTab] = useState<MobileTab>('Calendar');

  return (
    <div className="flex h-[100dvh] flex-col items-center px-6 pb-8 pt-16 sm:px-6 md:pt-32 lg:px-8">
      <div className="flex w-full max-w-7xl flex-grow flex-col overflow-hidden rounded-xl bg-white dark:bg-black">
        <div className="flex min-h-0 flex-grow flex-col space-y-3 p-2 sm:space-y-4 sm:p-3">
          {/* Mobile Tabs */}
          <div className="mt-4 flex justify-center md:hidden">
            <TabButton
              label="Calendar"
              isActive={mobileTab === 'Calendar'}
              onClick={() => setMobileTab('Calendar')}
              position="left"
            />
            <TabButton
              label="Status"
              isActive={mobileTab === 'Status'}
              onClick={() => setMobileTab('Status')}
              position="middle"
            />
            <TabButton
              label="Controls"
              isActive={mobileTab === 'Controls'}
              onClick={() => setMobileTab('Controls')}
              position="right"
            />
          </div>

          {/* Content Area */}
          <div className="min-h-0 flex-grow">
            {/* Mobile Content */}
            <div className="h-full md:hidden">
              {mobileTab === 'Controls' && (
                <CalendarControls
                  availableFilter={availableFilter}
                  selectedDaysOfWeek={selectedDaysOfWeek}
                  handleDayOfWeekToggle={handleDayOfWeekToggle}
                  handleAvailableFilterChange={handleAvailableFilterChange}
                  statusViewFilter={statusViewFilter}
                  setStatusViewFilter={setStatusViewFilter}
                />
              )}
              {mobileTab === 'Calendar' && (
                <CalendarView
                  dateRangeStatus={statusFilteredDates}
                  handleDayClick={handleDayClick}
                  setActiveMonth={setActiveMonth}
                />
              )}
              {mobileTab === 'Status' && (
                <CalendarStatus
                  dateRangeStatus={statusFilteredDates}
                  handleDayClick={handleDayClick}
                  activeMonth={activeMonth}
                  recentlyClickedDate={recentlyClickedDate}
                  availableFilter={availableFilter}
                  selectedDaysOfWeek={selectedDaysOfWeek}
                />
              )}
            </div>

            {/* Desktop Content */}
            <div className="hidden h-full flex-col md:flex">
              <div className="flex min-h-0 flex-grow flex-col space-y-4">
                <CalendarControls
                  availableFilter={availableFilter}
                  selectedDaysOfWeek={selectedDaysOfWeek}
                  handleDayOfWeekToggle={handleDayOfWeekToggle}
                  handleAvailableFilterChange={handleAvailableFilterChange}
                  statusViewFilter={statusViewFilter}
                  setStatusViewFilter={setStatusViewFilter}
                />
                <div className="flex min-h-0 flex-grow flex-row gap-4">
                  <CalendarView
                    dateRangeStatus={statusFilteredDates}
                    handleDayClick={handleDayClick}
                    setActiveMonth={setActiveMonth}
                  />
                  <CalendarStatus
                    dateRangeStatus={statusFilteredDates}
                    handleDayClick={handleDayClick}
                    activeMonth={activeMonth}
                    recentlyClickedDate={recentlyClickedDate}
                    availableFilter={availableFilter}
                    selectedDaysOfWeek={selectedDaysOfWeek}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
